import type { ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import { Component } from 'react';

interface ErrorBoundaryState {
  error: Error | null;
}

interface ErrorBoundaryProps extends PropsWithChildren {
  fallback?: ReactNode;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // TODO: ship to telemetry (Sentry / OTel collector) when wired in Phase D.
    // biome-ignore lint/suspicious/noConsole: error boundary must surface unhandled errors locally
    console.error('ErrorBoundary caught:', error, info);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        this.props.fallback ?? (
          <div className="flex min-h-screen flex-col items-center justify-center gap-2 p-8">
            <h1 className="text-xl font-semibold text-foreground">Something went wrong.</h1>
            <p className="text-sm text-muted-foreground">The page hit an error. Please refresh.</p>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
